<template>
  <div class="font-size-body">
    <v-text-field
      id="search-input"
      class="search-input m-l-5 m-r-5"
      label="Search components"
      v-model="word"
      prepend-inner-icon="fa-search"
      persistent-hint
      rounded
      singleLine
      clearable
      clear-icon="fa-times"
      dense
      hide-details
      @click:clear="clearWord"
      v-on:keydown.enter.exact.prevent
      v-on:keydown.enter.exact="searchWord"
    ></v-text-field>
    <div class="font-result m-t-10 m-l-10">{{ searchResultText }}</div>

    <sl-vue-tree
      ref="refSlTree"
      v-model="nodes"
      class="m-t-5 m-l-10 font-size-body search"
      :allowMultiselect="false"
      @beforedrop="beforedrop"
      @drop="drop"
    >
      <template slot="title" slot-scope="{ node }">
        <span
          v-if="!node.isLeaf"
          class="cursor-pointer m-l-3"
          :class="node.title === tabSelected ? 'selected' : ''"
          @click="pageClick(node.title)"
        >
          {{ node.title }}
        </span>
        <span
          v-else
          class="cursor-pointer"
          :class="node.data.id === nodeSelected.id ? 'component-selected' : ''"
          @click="componentClick(node.data.page, node.data.id)"
        >
          <span class="div-icon">
            <v-icon v-if="node.data && node.data.icon" class="component-icon">
              {{ node.data.icon }}
            </v-icon>
          </span>
          {{ node.title }}
        </span>
      </template>

      <template slot="toggle" slot-scope="{ node }">
        <span v-if="!node.isLeaf">
          <i v-if="node.isExpanded" class="fa fa-caret-down page-icon"></i>
          <i v-if="!node.isExpanded" class="fa fa-caret-right page-icon"></i>
        </span>
      </template>
    </sl-vue-tree>
  </div>
</template>

<script>
import SlVueTree from 'sl-vue-tree'
import { searchPage, searchComponent } from '@/views/flow/builder/utils/search'

import apiPrompt from '@/apis/prompt'

import 'sl-vue-tree/dist/sl-vue-tree-minimal.css'

export default {
  components: {
    SlVueTree
  },
  props: {
    ui: Object,
    currentTab: String,
    tabSelected: String,
    nodeSelected: Object
  },
  watch: {
    ui: {
      deep: true,
      handler() {
        const isEqual = _.isEqual(this.ui, this.tempUi)
        if (!isEqual) {
          this.searchWord()
          this.tempUi = { ...this.ui }
        }
      }
    },
    currentTab: {
      handler(val) {
        if (val === 'Search') {
          setTimeout(() => {
            const div = document.getElementById('search-input')
            if (div) div.focus()
          }, 100)
        }
      }
    }
  },
  data() {
    return {
      voicePaths: [],
      tempUi: {},
      tempNodes: [],
      nodes: [],
      word: '',
      searchResult: {
        component: 0,
        page: 0
      },
      searchResultText: ''
    }
  },
  created() {
    this.getVoicePath()
  },
  methods: {
    async getVoicePath() {
      try {
        this.voicePaths = []
        const getVoicePath = await apiPrompt.getVoicePath()
        const { data } = getVoicePath
        for (let i = 0; i < data.length; i++) {
          const obj = {}
          obj._id = data[i]._id ? data[i]._id : ''
          obj.text = data[i].text ? data[i].text : ''
          obj.path = data[i].path ? data[i].path : ''
          this.voicePaths.push(obj)
        }
      } catch (error) {}
    },
    beforedrop() {
      this.tempNodes = [...this.nodes]
    },
    drop(node) {
      this.nodes = [...this.tempNodes]
    },
    pageClick(page) {
      this.$emit('pageClick', page)
    },
    componentClick(page, componentId) {
      this.$emit('componentClick', { page, componentId })
    },
    clearWord() {
      this.word = ''
      this.nodes = []
      this.searchResult = {
        component: 0,
        page: 0
      }
      this.searchResultText = ''
    },
    searchWord() {
      this.nodes = []
      this.searchResult = {
        component: 0,
        page: 0
      }
      this.searchResultText = ''

      if (!this.word) return
      if (!this.ui || !this.ui.drawflow) return

      const search = JSON.parse(JSON.stringify(this.ui.drawflow))

      for (const [page, components] of Object.entries(search)) {
        const children = []
        for (const [id, component] of Object.entries(components.data)) {
          if (
            searchComponent(component, this.word, {
              voicePaths: this.voicePaths
            })
          ) {
            children.push({
              title: this.subString(component.data.name, 30),
              isLeaf: true,
              data: { page, id: component.id, icon: component.data.ui.icon }
            })
            this.searchResult.component += 1
          }
        }

        if (searchPage(page, this.word) || children.length > 0) {
          this.nodes.push({
            title: page,
            isLeaf: false,
            children
          })

          this.searchResult.page += 1
        }
      }

      this.nodes = this.sortingByTypeAndName(this.nodes)

      if (this.searchResult.component === 0 && this.searchResult.page === 0)
        this.searchResultText = 'No results found'
      else
        this.searchResultText = `Found ${this.searchResult.component} components in ${this.searchResult.page} pages`
    },
    subString(text, length) {
      if (text.length < length) return text
      return `${text.substr(0, length)}...`
    },
    sortingByTypeAndName(data) {
      data = _.orderBy(
        data,
        ['isLeaf', data => data.title.toLowerCase()],
        ['asc', 'asc']
      )
      for (let i = 0; i < data.length; i++) {
        if (data[i] && data[i].children) {
          data[i].children = this.sortingByTypeAndName(data[i].children)
        }
      }

      return data
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep .v-text-field--rounded > .v-input__control > .v-input__slot {
  padding: 0 10px !important;
}

.font-result {
  font-size: 12px;
  font-style: italic;
}

::v-deep .sl-vue-tree-root > .sl-vue-tree-nodes-list {
  max-height: calc(100vh - 245px);
  overflow-y: overlay;

  &::-webkit-scrollbar {
    display: none;
  }

  .sl-vue-tree-node {
    line-height: 2;
  }
}

::v-deep .sl-vue-tree-sidebar {
  width: 30px;
  text-align: right;
}

::v-deep .sl-vue-tree-selected .sl-vue-tree-node-item {
  background: none !important;
}

.div-icon {
  display: inline-block;
  width: 23px;
  text-align: center;
}
.page-icon {
  font-size: 18px;
}

.component-icon {
  font-size: 16px;
}

.selected,
.component-selected {
  font-weight: 700;
  color: $primary !important;

  .v-icon {
    color: $primary !important;
  }
}
</style>
